<script setup lang="ts">
import { switchLocaleAndNotify } from "~/utils/categoryI18n";
import type { AppearanceMode } from "~/composables/useAppearanceSwitch";
import HeaderMenuActions from "~/components/header/HeaderMenuActions.vue";

const props = defineProps<{
  locale: string;
  languages?: { value: string; label: string }[];
  /**
   * How to apply a chosen locale. The marketing site switches the URL prefix;
   * /start re-fetches with a new `?locale=` and must NOT move off its path,
   * so it passes its own.
   */
  onLanguage?: (code: string) => void | Promise<void>;
  /** Hide the Appearance submenu — a whitelabel flow owns its single theme. */
  hideAppearance?: boolean;
}>();

const route = useRoute();
const { user, isAuthenticated, signIn, signOut } = useAuth();
const { mode, setMode } = useAppearanceSwitch();

// Only the Sign in ROW depends on the BFF being up — with auth off there is no
// login to offer and a dead button would just confuse. The menu itself still
// carries language, appearance and the support links, which are useful to a
// signed-out visitor and are the whole reason /start uses this control.
const bffEnabled = useRuntimeConfig().public.bffEnabled;

// `menu-compact="auto"`: on touch / small viewports the Language and Appearance
// submenus swap into the same panel instead of flying out. The mobile drawer
// mounts this menu at the left edge, where a `left-start` flyout lands
// off-screen.

const labels = computed(() => {
  const dict: Record<string, Record<string, string>> = {
    en: {
      signIn: "Sign in",
      signOut: "Sign out",
      account: "Account",
      language: "Language",
      appearance: "Appearance",
      light: "Light",
      dark: "Dark",
      auto: "System",
      support: "Support",
      report: "Report an issue",
      logOut: "Log out",
      menu: "Settings",
    },
    nl: {
      signIn: "Inloggen",
      signOut: "Uitloggen",
      account: "Account",
      language: "Taal",
      appearance: "Weergave",
      light: "Licht",
      dark: "Donker",
      auto: "Systeem",
      support: "Support",
      report: "Probleem melden",
      logOut: "Uitloggen",
      menu: "Instellingen",
    },
  };
  return dict[props.locale] ?? dict.en!;
});

/**
 * External destinations. Opened with `window.open` rather than an `<a>`: a
 * dropdown item is a button that reports a `value`, not a link, so there is no
 * href to hang on it. `noopener` is not optional — without it the opened tab
 * gets a handle on `window.opener`.
 */
const EXTERNAL: Record<string, string> = {
  support: "https://support.econnect.eu",
  "report-issue": "https://desk.econnect.eu/portal/nl/newticket",
};

/**
 * Rebuild the whole control when the auth state flips.
 *
 * A GUARD, not a verified fix: Sign in and Sign out have been seen together,
 * rarely, and neither of us could reproduce it on demand — four attempts
 * (mock sign-in, a client-side session resolve, the same with this key removed,
 * and an HMR update with the menu mounted) all came back clean.
 *
 * `<e-account-menu>` REPARENTS the items it is given: on slotchange it moves
 * them out of this component's light DOM and into the dropdown list inside its
 * own shadow root. Vue still believes they live where it put them, so when a
 * `v-if` turns off, the removal targets a parent that no longer holds the node
 * and the row simply stays — which is how you end up looking at Sign in and
 * Sign out at the same time, the new row appended below the stale one.
 *
 * Keying the element sidesteps the pattern entirely: Vue never adds or removes
 * a row, it drops the custom element and mounts a fresh one that builds its
 * list from the items it is actually given. The flip is rare and today usually
 * arrives with a full navigation anyway, so the remount costs nothing a visitor
 * can perceive — which is why this is worth keeping even unproven.
 *
 * If it is seen again, the question to settle first is whether the stale row
 * sits in this component's light DOM (Vue's problem) or in the menu's shadow
 * list (the reparenting trap, and then this is the right fix).
 *
 * The underlying fragility is reported upstream —
 * Ui `.ai/bug-reports/2026-10-01-lightdom-items-lose-their-row-when-a-framework-patches-text.md`
 * is the same root cause seen from the text side.
 */
const menuKey = computed(() => (isAuthenticated.value ? "authed" : "anon"));

const accountName = computed(() => user.value?.name ?? user.value?.email ?? "");
const accountEmail = computed(() => user.value?.email ?? "");
// OIDC `picture` claim (an images.econnect.eu URL). Only https URLs: the claim
// is IdP data, but it lands in an <img src>. `avatar-src` is inert until
// ui-core ships it (Ui feature request 2026-10-07-account-menu-avatar-src);
// <e-avatar> falls back to initials when the image fails.
const accountPicture = computed(() => {
  const picture = user.value?.claims.picture;
  return typeof picture === "string" && picture.startsWith("https://") ? picture : undefined;
});

// ONE control for both states. Signed out there is no name, no email and no
// avatar image, so <e-avatar>'s fallback chain lands on its icon — which is
// exactly the anonymous affordance, in the same place, at the same size, as the
// avatar it becomes once you sign in.
function onSignIn() {
  // Top-level navigation to the BFF, which runs the OIDC dance server-side
  // and writes the HttpOnly session cookie before redirecting back here.
  signIn({ redirect: route.fullPath });
}

/**
 * One handler for every menu action, keyed by the item's `value`. Submenu items
 * report through the same root event, so language and appearance arrive here
 * too — prefixed, so a locale code can never collide with an action name.
 */
async function onAccountSelect(e: Event) {
  const value = (e as CustomEvent<{ value?: string }>).detail?.value;
  if (!value) return;

  if (value === "sign-in") return onSignIn();
  // Back to the page you signed out ON, not to the homepage: `signOut()`
  // defaults its returnUrl to "/", which silently threw away wherever you
  // were. A page that needs auth bounces to the sign-in interstitial from the
  // global middleware, which is the right landing for that case.
  if (value === "sign-out") return void signOut(undefined, route.fullPath);

  const external = EXTERNAL[value];
  if (external) return void window.open(external, "_blank", "noopener");

  if (value.startsWith("appearance:")) {
    return setMode(value.slice("appearance:".length) as AppearanceMode);
  }

  if (value.startsWith("lang:")) {
    const code = value.slice("lang:".length);
    if (!code || code === props.locale) return;
    if (props.onLanguage) return void props.onLanguage(code);
    await switchLocaleAndNotify(route.path, code);
  }
}
</script>

<template>
  <e-account-menu
    :key="menuKey"
    :name="accountName"
    :email="accountEmail"
    :avatar-src="accountPicture"
    collapsed
    placement="bottom-end"
    avatar-color="accent"
    tooltip-placement="bottom"
    tooltip-size="xs"
    tooltip-color="mono"
    tooltip-variant="solid"
    menu-compact="auto"
    :aria-label="isAuthenticated ? labels.account : labels.menu"
    @account-menu:select="onAccountSelect"
  >
    <template v-if="bffEnabled && !isAuthenticated">
      <e-dropdown-menu-item
        value="sign-in"
        leading-icon="login"
        color="mono"
        variant="solid"
      >
        <span>{{ labels.signIn }}</span>
      </e-dropdown-menu-item>
      <e-dropdown-menu-separator />
    </template>

    <HeaderMenuActions
      :locale="locale"
      :languages="languages"
      :mode="mode"
      :labels="labels"
      :hide-appearance="hideAppearance"
    />

    <template v-if="bffEnabled && isAuthenticated">
      <e-dropdown-menu-separator />
      <e-dropdown-menu-item value="sign-out" leading-icon="logout">
        <span>{{ labels.logOut }}</span>
      </e-dropdown-menu-item>
    </template>
  </e-account-menu>
</template>
